/**
 * Alert Quick Widget - Phase 15
 * 
 * Lightweight alert widget for dashboard header or sidebar.
 */

import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, XCircle, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getAlertSummary, getActiveAlerts } from '@/lib/reliabilityAlerts/alertStore';
import { runEvaluationCycle } from '@/lib/reliabilityAlerts/alertEngine';
import { ALERT_STATUS } from '@/lib/reliabilityAlerts/alertModel';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

export function AlertQuickWidget({ className = '', showEvaluate = false }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [criticalAlerts, setCriticalAlerts] = useState([]);

  useEffect(() => {
    loadAlerts();
    
    // Poll every minute
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      const [summaryData, alerts] = await Promise.all([
        getAlertSummary({}),
        getActiveAlerts({ severity: 'critical', openOnly: true })
      ]);
      
      setSummary(summaryData);
      setCriticalAlerts(alerts.slice(0, 3));
    } catch (error) {
      console.error('[AlertWidget] Failed to load:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluate = async () => {
    setLoading(true);
    try {
      await runEvaluationCycle();
      await loadAlerts();
    } catch (error) {
      console.error('[AlertWidget] Evaluation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCount = summary?.open || 0;
  const criticalCount = summary?.critical || 0;
  const escalatedCount = summary?.escalated || 0;

  // Determine widget urgency styling
  const getUrgencyStyle = () => {
    if (criticalCount > 0) return 'border-red-500 border-2';
    if (openCount > 5) return 'border-yellow-500 border';
    return '';
  };

  if (loading && !summary) {
    return (
      <Card className={className} data-testid="alert-widget-loading">
        <CardContent className="pt-6 flex items-center justify-center min-h-[100px]">
          <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${className} ${getUrgencyStyle()}`} data-testid="alert-quick-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Bell className={`h-4 w-4 ${criticalCount > 0 ? 'text-red-500' : 'text-blue-500'}`} />
            Alerts
          </CardTitle>
          {showEvaluate && (
            <Button variant="ghost" size="sm" onClick={handleEvaluate} disabled={loading}>
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <p className={`text-lg font-bold ${openCount > 0 ? 'text-red-600' : ''}`}>{openCount}</p>
              <p className="text-xs text-gray-500">Open</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <p className={`text-lg font-bold ${criticalCount > 0 ? 'text-red-600' : ''}`}>{criticalCount}</p>
              <p className="text-xs text-gray-500">Critical</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <p className={`text-lg font-bold ${escalatedCount > 0 ? 'text-purple-600' : ''}`}>{escalatedCount}</p>
              <p className="text-xs text-gray-500">Escalated</p>
            </div>
          </div>

          {/* Critical Alerts Preview */}
          {criticalAlerts.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                Critical Alerts
              </p>
              {criticalAlerts.map(alert => (
                <div 
                  key={alert.alertId}
                  className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs"
                >
                  <p className="font-medium truncate">{alert.title}</p>
                  <p className="text-gray-500 truncate">{alert.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* Link to Alert Center */}
          <a
            href={bo('reliability-alerts')}
            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
            data-testid="alert-widget-link"
          >
            <span>View All Alerts</span>
            <ChevronRight className="h-4 w-4" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Alert Header Strip - Compact alert indicator for header bar
 */
export function AlertHeaderStrip({ className = '' }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAlertSummary({});
        setSummary(data);
      } catch (e) {
        console.error('[AlertHeaderStrip]', e);
      }
    };
    
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  const criticalCount = summary?.critical || 0;
  const openCount = summary?.open || 0;

  if (criticalCount === 0 && openCount === 0) {
    return null;
  }

  return (
    <a
      href={bo('reliability-alerts')}
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        criticalCount > 0 
          ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400'
      } ${className}`}
      data-testid="alert-header-strip"
    >
      {criticalCount > 0 ? (
        <>
          <XCircle className="h-3 w-3" />
          {criticalCount} Critical
        </>
      ) : (
        <>
          <AlertTriangle className="h-3 w-3" />
          {openCount} Alert{openCount !== 1 ? 's' : ''}
        </>
      )}
    </a>
  );
}

export default AlertQuickWidget;
